import React, { useState, useEffect } from 'react';
import { FaPlus, FaTimes, FaEdit, FaGripLines } from 'react-icons/fa';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import './TagManagement.css';

const TagManagement = () => {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTagForm, setShowTagForm] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [formMode, setFormMode] = useState('create'); // 'create' или 'edit'
  
  // Состояние формы
  const [tagName, setTagName] = useState('');
  const [tagDescription, setTagDescription] = useState('');
  const [tagIsActive, setTagIsActive] = useState(true); // Новое состояние для активности тега
  // Используем фиксированный цвет для всех тегов
  const fixedTagColor = '#535bf2';
  
  // Загрузка списка тегов
  useEffect(() => {
    fetchTags();
  }, []);

  // Настройка сенсоров для drag-and-drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      // Используем delay вместо distance для лучшей поддержки мобильных устройств
      activationConstraint: {
        delay: 250, // задержка перед началом перетаскивания в мс
        tolerance: 5, // небольшая погрешность для предотвращения случайных драгов
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  
  // Функция для загрузки тегов
  const fetchTags = async () => {
    setLoading(true);
    try {
      const telegramId = localStorage.getItem('telegram_id');
      const response = await fetch('/api/tags?sort=sortOrder', {
        headers: {
          'X-Telegram-ID': telegramId
        }
      });
      
      if (!response.ok) {
        throw new Error('Ошибка при загрузке тегов');
      }
      
      const tagsData = await response.json();
      setTags(tagsData);
    } catch (error) {
      console.error('Ошибка при загрузке тегов:', error);
    } finally {
      setLoading(false);
    }
  };

  // Обработчик окончания перетаскивания
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (active.id !== over.id) {
      // Находим индексы элементов
      const oldIndex = tags.findIndex(tag => tag._id === active.id);
      const newIndex = tags.findIndex(tag => tag._id === over.id);
      
      // Создаем новый массив с измененным порядком
      const updatedTags = [...tags];
      const [removedTag] = updatedTags.splice(oldIndex, 1);
      updatedTags.splice(newIndex, 0, removedTag);
      
      // Обновляем состояние
      setTags(updatedTags);
      
      // Обновляем sortOrder для всех тегов
      try {
        const telegramId = localStorage.getItem('telegram_id');
        
        // Отправляем запросы для обновления каждого тега
        const updatePromises = updatedTags.map((tag, index) => {
          return fetch(`/api/tags/${tag._id}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'X-Telegram-ID': telegramId
            },
            body: JSON.stringify({ 
              sortOrder: index,
              name: tag.name,
              description: tag.description || '',
              color: tag.color || fixedTagColor
            })
          });
        });
        
        // Ожидаем завершения всех запросов
        await Promise.all(updatePromises);
        
      } catch (error) {
        console.error('Ошибка при обновлении порядка тегов:', error);
        // Восстанавливаем исходный порядок при ошибке
        fetchTags();
      }
    }
  };
  
  // Обработчик создания нового тега
  const handleAddTag = () => {
    setEditingTag(null);
    setFormMode('create');
    setTagName('');
    setTagDescription('');
    setTagIsActive(true); // По умолчанию новый тег активен
    setShowTagForm(true);
  };
  
  // Обработчик редактирования тега
  const handleEditTag = (tag) => {
    setEditingTag(tag);
    setFormMode('edit');
    setTagName(tag.name);
    setTagDescription(tag.description || '');
    setTagIsActive(tag.isActive !== undefined ? tag.isActive : true); // Устанавливаем текущий статус активности
    setShowTagForm(true);
  };
  
  // Обработчик отмены формы
  const handleCancelForm = () => {
    setShowTagForm(false);
    setEditingTag(null);
  };
  
  // Обработчик сохранения тега
  const handleSaveTag = async (e) => {
    e.preventDefault();
    
    if (!tagName.trim()) {
      alert('Название тега обязательно');
      return;
    }
    
    try {
      const telegramId = localStorage.getItem('telegram_id');
      const headers = {
        'Content-Type': 'application/json',
        'X-Telegram-ID': telegramId
      };
      
      const tagData = {
        name: tagName.trim(),
        description: tagDescription.trim(),
        color: fixedTagColor, // Используем фиксированный цвет
        isActive: tagIsActive // Добавляем статус активности
      };
      
      let url = '/api/tags';
      let method = 'POST';
      
      // Если это редактирование, меняем метод и URL
      if (formMode === 'edit' && editingTag) {
        url = `/api/tags/${editingTag._id}`;
        method = 'PUT';
      }
      
      const response = await fetch(url, {
        method,
        headers,
        body: JSON.stringify(tagData)
      });
      
      if (!response.ok) {
        throw new Error(`Ошибка при ${formMode === 'create' ? 'создании' : 'обновлении'} тега`);
      }
      
      // Перезагружаем список тегов
      fetchTags();
      
      // Закрываем форму
      setShowTagForm(false);
      setEditingTag(null);
      
      alert(`Тег успешно ${formMode === 'create' ? 'создан' : 'обновлен'}`);
    } catch (error) {
      console.error('Ошибка при сохранении тега:', error);
      alert(error.message);
    }
  };
  
  // Обработчик удаления тега
  const handleDeleteTag = async (tag) => {
    // Проверяем, является ли тег подарочным - его нельзя удалить
    if (tag.isGiftTag) {
      alert('Специальный тег "Подарки" нельзя удалить. Его можно только деактивировать.');
      return;
    }

    // Проверяем, используется ли тег
    if (tag.contentCount > 0) {
      alert(`Невозможно удалить тег "${tag.name}", так как он используется в ${tag.contentCount} постах. Сначала удалите его из постов.`);
      return;
    }
    
    const confirmDelete = window.confirm(`Вы действительно хотите удалить тег "${tag.name}"?`);
    if (!confirmDelete) return;
    
    try {
      const telegramId = localStorage.getItem('telegram_id');
      const response = await fetch(`/api/tags/${tag._id}`, {
        method: 'DELETE',
        headers: {
          'X-Telegram-ID': telegramId
        }
      });
      
      if (!response.ok) {
        throw new Error('Ошибка при удалении тега');
      }
      
      // Обновляем список тегов
      setTags(tags.filter(t => t._id !== tag._id));
      
      alert('Тег успешно удален');
    } catch (error) {
      console.error('Ошибка при удалении тега:', error);
      alert(error.message);
    }
  };
  
  return (
    <div className="tag-management">
      {showTagForm ? (
        <div className="tag-form-container">
          <h3>{formMode === 'create' ? 'Создание тега' : 'Редактирование тега'}</h3>
          <form onSubmit={handleSaveTag} className="tag-form">
            <div className="form-group">
              <label htmlFor="tagName">Название тега*:</label>
              <input
                type="text"
                id="tagName"
                value={tagName}
                onChange={(e) => setTagName(e.target.value)}
                placeholder="Введите название тега"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="tagDescription">Описание тега:</label>
              <textarea
                id="tagDescription"
                value={tagDescription}
                onChange={(e) => setTagDescription(e.target.value)}
                placeholder="Введите описание тега (необязательно)"
              />
            </div>
            <div className="form-group form-group-checkbox">
              <label htmlFor="tagIsActive">
                <input
                  type="checkbox"
                  id="tagIsActive"
                  checked={tagIsActive}
                  onChange={(e) => setTagIsActive(e.target.checked)}
                />
                Активен (виден пользователям)
              </label>
            </div>
            {/* Убрали выбор цвета */}
            <div className="form-buttons">
              <button type="submit" className="submit-button">
                {formMode === 'create' ? 'Создать тег' : 'Сохранить изменения'}
              </button>
              <button type="button" className="cancel-button" onClick={handleCancelForm}>
                Отмена
              </button>
            </div>
          </form>
        </div>
      ) : (
        <>
          <div className="tag-header">
            <button className="add-button" onClick={handleAddTag}>
              <FaPlus /> Создать тег
            </button>
          </div>
          
          {loading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Загрузка тегов...</p>
            </div>
          ) : (
            <div className="tags-list">
              {tags.length === 0 ? (
                <p className="no-tags-message">Теги еще не созданы. Нажмите "Создать тег", чтобы добавить первый тег.</p>
              ) : (
                <DndContext 
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={tags.map(tag => tag._id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {tags.map(tag => (
                      <SortableTagItem 
                        key={tag._id} 
                        tag={tag}
                        tagColor={fixedTagColor}
                        onEdit={handleEditTag}
                        onDelete={handleDeleteTag}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Компонент элемента тега с поддержкой drag-and-drop
const SortableTagItem = ({ tag, tagColor, onEdit, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: tag._id });
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
    position: 'relative'
  };

  return (
    <div ref={setNodeRef} style={style} className="tag-item">
      <div className="tag-info">
        <div 
          {...attributes} 
          {...listeners} 
          className="drag-handle" 
          style={{ 
            cursor: 'grab', 
            marginRight: '10px',
            padding: '10px', // Увеличиваем область захвата
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            touchAction: 'none' // Важно для работы на touch-устройствах
          }}
        >
          <FaGripLines size={20} />
        </div>
        <div className="tag-details">
          <div className="tag-name">
            <strong>{tag.name}</strong> {tag.isGiftTag && '🎁'} {/* Иконка для подарочного тега */}
            {!tag.isActive && <span className="tag-status-inactive">(Неактивен)</span>}
          </div>
          {tag.description && (
            <div className="tag-description">{tag.description}</div>
          )}
          <div className="tag-stats">
            Используется в {tag.contentCount || 0} постах
          </div>
        </div>
      </div>
      <div className="tag-actions">
        <button 
          className="tag-action-button edit" 
          onClick={() => onEdit(tag)}
          title="Редактировать тег"
        >
          <FaEdit />
        </button>
        {!tag.isGiftTag && ( // Кнопка удаления только для обычных тегов
          <button 
            className="tag-action-button delete" 
            onClick={() => onDelete(tag)}
            title="Удалить тег"
            disabled={tag.contentCount > 0} // Блокируем, если используется
          >
            <FaTimes />
          </button>
        )}
        {tag.isGiftTag && (
          <span title="Этот тег нельзя удалить" className="tag-action-placeholder"></span>
        )}
      </div>
    </div>
  );
};

export default TagManagement;
